/**
 * Output Path Picker - selects a value inside an upstream step's output, field by field.
 *
 * Driven by the upstream agent's JSON output_schema: each level offers that object's fields,
 * and a list field gets an extra "which item" choice (all items, the Nth, the last, ...).
 * Produces a dotted path the backend resolves with lookup_result_field (e.g. "issues.2.title").
 * Falls back to a free-text path when the upstream has no schema.
 */

import React, { useEffect, useState } from 'react';
import { agentApi } from '../api/client';
import { OutputFormat } from '../types/agent';
import {
  RAW_RESPONSE_FIELD,
  childFields,
  childSchema,
  describePath,
  joinPath,
  ordinal,
  parsePath,
  stepType,
  type OutputPathField,
  type PathStep,
} from '../utils/outputPath';
import './OutputPathPicker.css';

type JsonSchema = Record<string, any>;

const PRESET_INDEXES = [0, 1, 2, 3, 4, -1];
const ALL_ITEMS = 'all';
const CUSTOM_INDEX = 'custom';

/** The JSON output_schema of the agent behind an upstream node, or undefined if it has none. */
export function useAgentOutputSchema(agentId: string | undefined): { schema: JsonSchema | undefined; loading: boolean } {
  const [schema, setSchema] = useState<JsonSchema | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSchema(undefined);
    if (!agentId) return;
    setLoading(true);
    agentApi
      .getAgent(agentId)
      .then((agent) => {
        // Only a structured agent's schema is enforced on its output (structured_output_schema
        // in raiqon/utils/json_schema.py) - a schema left over from switching formats isn't.
        const outputSchema = agent.output?.format === OutputFormat.STRUCTURED ? agent.output.output_schema : undefined;
        if (!cancelled && outputSchema?.properties) setSchema(outputSchema);
      })
      .catch(() => {
        // No schema - the picker falls back to a free-text path.
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  return { schema, loading };
}

function typeHint(field: OutputPathField): string {
  return field.type === 'array' ? ' [list]' : field.type === 'object' ? ' {…}' : '';
}

interface ItemSelectProps {
  index: number | undefined;
  onChange: (index: number | undefined) => void;
}

const ItemSelect: React.FC<ItemSelectProps> = ({ index, onChange }) => {
  const [custom, setCustom] = useState(index !== undefined && !PRESET_INDEXES.includes(index));
  const selectValue = custom ? CUSTOM_INDEX : index === undefined ? ALL_ITEMS : String(index);

  return (
    <div className="output-path-item">
      <span className="output-path-item-label">Which item</span>
      <select
        className="output-path-select"
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          setCustom(v === CUSTOM_INDEX);
          if (v === ALL_ITEMS) onChange(undefined);
          else if (v === CUSTOM_INDEX) onChange(index !== undefined && index >= 0 ? index : PRESET_INDEXES.length - 1);
          else onChange(Number(v));
        }}
      >
        <option value={ALL_ITEMS}>All items (whole list)</option>
        {PRESET_INDEXES.map((i) => (
          <option key={i} value={String(i)}>
            {i === -1 ? 'Last item' : `${ordinal(i + 1)} item`}
          </option>
        ))}
        <option value={CUSTOM_INDEX}>Item number…</option>
      </select>
      {custom && (
        <input
          type="number"
          min={1}
          className="output-path-index"
          // Shown 1-based ("3rd item" = 3), stored 0-based like the backend expects.
          value={index !== undefined && index >= 0 ? index + 1 : ''}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            if (n >= 1) onChange(n - 1);
          }}
          aria-label="Item number (1 = first)"
        />
      )}
    </div>
  );
};

interface OutputPathPickerProps {
  value: string;
  onChange: (path: string) => void;
  schema: JsonSchema | undefined;
  loading?: boolean;
  // Offer the agent's full raw text ("response") as a top-level choice.
  includeRawResponse?: boolean;
  placeholder?: string;
}

export const OutputPathPicker: React.FC<OutputPathPickerProps> = ({
  value,
  onChange,
  schema,
  loading = false,
  includeRawResponse = true,
  placeholder = 'Output field, e.g. issues.0.title',
}) => {
  const [editAsText, setEditAsText] = useState(false);
  const steps = parsePath(value, schema);
  const rootFields = childFields(schema);

  if (!schema || editAsText) {
    return (
      <div className="output-path-picker">
        <input
          type="text"
          className="output-path-select output-path-text"
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder={loading ? 'Loading output fields…' : placeholder}
          spellCheck={false}
        />
        <div className="output-path-footer">
          <span className="output-path-hint">
            {schema || loading
              ? 'Dot-separated; a number picks a list item (0 = first, -1 = last).'
              : 'Set the upstream agent to Structured output to pick fields from its schema.'}
          </span>
          {schema && (
            <button type="button" className="output-path-toggle" onClick={() => setEditAsText(false)}>
              Pick from fields
            </button>
          )}
        </div>
      </div>
    );
  }

  const setStep = (level: number, step: PathStep | null) => {
    const next = steps.slice(0, level);
    if (step) next.push(step);
    onChange(joinPath(next));
  };

  // One field select per level, each followed by an item select when it lands on a list.
  const levels: React.ReactNode[] = [];
  for (let level = 0; level <= steps.length; level++) {
    const parent = level === 0 ? undefined : steps[level - 1];
    const fields = level === 0 ? rootFields : childFields(parent?.schema);
    // A list item / object with no further fields ends the chain.
    if (level > 0 && fields.length === 0) break;

    const current = steps[level];
    const known = current && fields.some((f) => f.name === current.field);
    levels.push(
      <div key={`field-${level}`} className="output-path-level">
        {level > 0 && <span className="output-path-arrow">›</span>}
        <select
          className="output-path-select"
          value={current?.field ?? ''}
          onChange={(e) => {
            const name = e.target.value;
            if (!name) return setStep(level, null);
            setStep(level, { field: name, schema: childSchema(level === 0 ? schema : parent?.schema, name) });
          }}
        >
          <option value="">{level === 0 ? 'Select a field…' : 'Whole value'}</option>
          {fields.map((f) => (
            <option key={f.name} value={f.name} title={f.description}>
              {f.name}
              {typeHint(f)}
            </option>
          ))}
          {level === 0 && includeRawResponse && !schema.properties?.[RAW_RESPONSE_FIELD] && (
            <option value={RAW_RESPONSE_FIELD}>Full raw response</option>
          )}
          {current && !known && !(level === 0 && current.field === RAW_RESPONSE_FIELD) && (
            <option value={current.field}>{current.field} (not in schema)</option>
          )}
        </select>
      </div>
    );

    if (current && stepType({ schema: current.schema }) === 'array') {
      levels.push(
        <ItemSelect
          key={`item-${level}`}
          index={current.index}
          onChange={(index) => {
            // Keep deeper selections when switching between items of the same list.
            const next = [...steps];
            next[level] = { ...current, index };
            onChange(joinPath(next));
          }}
        />
      );
    }
  }

  return (
    <div className="output-path-picker">
      {levels}
      <div className="output-path-footer">
        {value ? (
          <span className="output-path-summary" title={value}>
            <code>{value}</code>
            <span className="output-path-hint">{describePath(value)}</span>
          </span>
        ) : (
          <span className="output-path-hint">{loading ? 'Loading output fields…' : ''}</span>
        )}
        <button type="button" className="output-path-toggle" onClick={() => setEditAsText(true)}>
          Edit as path
        </button>
      </div>
    </div>
  );
};
